// packages/agent-kit/src/hooks/billing/useMonobankTopup.ts

import { useState, useCallback } from 'react';
import { billingService, isApiError } from '@dopomogai/api-client';
import type { CreateTopupInvoicePayload, CreateTopupInvoiceResponse } from '@dopomogai/types';
import { AgentKitError } from '../types';

/**
 * A React hook for creating a Monobank top-up invoice.
 * This handles the mutation state (loading, error) and redirects the user
 * to the Monobank payment page upon success.
 *
 * @example
 * const { createInvoice, isLoading, error } = useMonobankTopup();
 * const handlePurchase = () => {
 *   createInvoice({ credits: 50 });
 * };
 */
export const useMonobankTopup = () => {
  const [data, setData] = useState<CreateTopupInvoiceResponse | null>(null);
  const [error, setError] = useState<AgentKitError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Triggers the API call to create the invoice.
   */
  const createInvoice = useCallback(async (payload: CreateTopupInvoicePayload) => {
    // Basic client-side validation
    if (!payload.credits || payload.credits < 5) {
      setError(new AgentKitError('Number of credits must be at least 5.'));
      return;
    }

    setIsLoading(true);
    setError(null);
    setData(null);

    const res = await billingService.createTopupInvoice(payload);

    if (isApiError(res)) {
      const err = new AgentKitError('Failed to create Monobank top-up invoice', res);
      setError(err);
      setIsLoading(false);
    } else {
      setData(res);
      // On success, redirect the user to the Monobank payment page (SSR-safe).
      if (typeof window !== 'undefined') {
        window.location.href = res.page_url;
      } else {
        // If window is not available, stop loading to avoid hanging state.
        setIsLoading(false);
      }
    }
  }, []);

  return { createInvoice, data, error, isLoading };
};